import React, { useState, useEffect } from 'react';
import { 
  AreaChart, 
  Area, 
  BarList, 
  Card, 
  Title, 
  Text,
  Flex,
  Grid,
  Metric,
  Badge,
  TabGroup,
  TabList,
  Tab,
  Select,
  SelectItem,
} from '@tremor/react';
import { Bot, Eye, ExternalLink, TrendingUp, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';

const TIME_RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function BrandAnalyticsDashboard({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [botCategory, setBotCategory] = useState('all');

  useEffect(() => {
    async function fetchAnalytics() {
      if (!projectId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const result = await apiFetch(`/api/analytics/brand/${projectId}/summary?range=${timeRange}`);
        setData(result);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchAnalytics();
  }, [projectId, timeRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-surface border-white/10">
        <Text className="text-red-400">Failed to load analytics: {error}</Text>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="bg-surface border-white/10">
        <Text className="text-slate-400">No analytics data available yet.</Text>
      </Card>
    );
  }

  const { bot_visits, page_views, outbound_clicks, trend, top_topics } = data;

  // Format bot data for BarList
  const botData = Object.entries(bot_visits.by_bot || {}).map(([name, value]) => ({
    name: formatBotName(name),
    value,
    icon: () => <Bot className="w-4 h-4 text-blue-400" />,
  }));

  // Filter by category if selected
  const filteredBotData = botCategory === 'all' 
    ? botData 
    : botData.filter(b => {
        const category = getBotCategory(b.name.toLowerCase());
        return category === botCategory;
      });

  // Format trend data for chart
  const chartData = (trend || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    'Bot Visits': d.bot_visits,
    'Page Views': d.page_views,
    'Website Clicks': d.outbound_clicks,
  }));

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-end">
        <Select value={timeRange} onValueChange={setTimeRange} className="w-40">
          {TIME_RANGES.map(r => (
            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
          ))}
        </Select>
      </div>

      {/* Stats Cards */}
      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <StatCard
          title="Bot Visits"
          value={bot_visits.total}
          icon={<Bot className="w-5 h-5" />}
          color="blue"
          subtitle={`LLM: ${bot_visits.by_category?.llm || 0} | Search: ${bot_visits.by_category?.search || 0}`}
        />
        <StatCard
          title="Page Views"
          value={page_views.total}
          icon={<Eye className="w-5 h-5" />}
          color="purple"
          subtitle={`${page_views.unique_sessions || 0} unique sessions`}
        />
        <StatCard
          title="Website Clicks"
          value={outbound_clicks.total}
          icon={<ExternalLink className="w-5 h-5" />}
          color="green"
          subtitle={page_views.total > 0 
            ? `${((outbound_clicks.total / page_views.total) * 100).toFixed(1)}% conversion`
            : 'No views yet'
          }
        />
        <StatCard
          title="Topics Tracked"
          value={top_topics?.length || 0}
          icon={<TrendingUp className="w-5 h-5" />}
          color="amber"
          subtitle="With traffic data"
        />
      </Grid>

      {/* Traffic Trend Chart */}
      <Card className="bg-surface border-white/10">
        <Title className="text-white mb-4">Traffic Attribution</Title>
        {chartData.length > 0 ? (
          <AreaChart
            className="h-72"
            data={chartData}
            index="date"
            categories={['Bot Visits', 'Page Views', 'Website Clicks']}
            colors={['blue', 'violet', 'emerald']}
            valueFormatter={(v) => v.toLocaleString()}
            showLegend={true}
            showGridLines={false}
            curveType="monotone"
          />
        ) : (
          <div className="h-72 flex items-center justify-center text-slate-500">
            No traffic data yet. Data will appear as bots and users visit your pages.
          </div>
        )}
      </Card>

      <Grid numItems={1} numItemsLg={2} className="gap-6">
        {/* Bot Activity */}
        <Card className="bg-surface border-white/10">
          <Flex justifyContent="between" alignItems="center" className="mb-4">
            <Title className="text-white">Bot Activity</Title>
            <TabGroup index={['all', 'llm', 'search'].indexOf(botCategory)} onIndexChange={(i) => setBotCategory(['all', 'llm', 'search'][i])}>
              <TabList variant="solid" className="bg-white/5">
                <Tab className="text-xs">All</Tab>
                <Tab className="text-xs">LLM</Tab>
                <Tab className="text-xs">Search</Tab>
              </TabList>
            </TabGroup>
          </Flex>
          {filteredBotData.length > 0 ? (
            <BarList
              data={filteredBotData}
              valueFormatter={(v) => v.toLocaleString()}
              className="mt-2"
            />
          ) : (
            <Text className="text-slate-500">No bot visits recorded yet.</Text>
          )}
        </Card>

        {/* Top Topics */}
        <Card className="bg-surface border-white/10">
          <Title className="text-white mb-4">Top Performing Topics</Title>
          {top_topics && top_topics.length > 0 ? (
            <div className="space-y-3">
              {top_topics.slice(0, 5).map((topic, i) => (
                <div key={topic.id || i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <Text className="text-white font-medium">{topic.title || 'Unknown Topic'}</Text>
                    <Text className="text-slate-500 text-xs">/{topic.slug}</Text>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-blue-400 font-bold">{topic.bot_visits}</div>
                      <div className="text-slate-500 text-xs">Bots</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-400 font-bold">{topic.page_views}</div>
                      <div className="text-slate-500 text-xs">Views</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Text className="text-slate-500">No topic traffic data yet.</Text>
          )}
        </Card>
      </Grid>

      {/* Page Type Breakdown */}
      <Card className="bg-surface border-white/10">
        <Title className="text-white mb-4">Traffic by Page Type</Title>
        <Grid numItems={1} numItemsSm={3} className="gap-4">
          <PageTypeCard
            type="Brand Page"
            botVisits={bot_visits.by_page_type?.brand || 0}
            pageViews={page_views.by_page_type?.brand || 0}
          />
          <PageTypeCard
            type="Topic Pages"
            botVisits={bot_visits.by_page_type?.topic || 0}
            pageViews={page_views.by_page_type?.topic || 0}
          />
          <PageTypeCard
            type="Insight Pages"
            botVisits={bot_visits.by_page_type?.insight || 0}
            pageViews={page_views.by_page_type?.insight || 0}
          />
        </Grid>
      </Card>
    </div>
  );
}

function StatCard({ title, value, icon, color, subtitle }) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    green: 'text-green-400 bg-green-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
  };

  return (
    <Card className="bg-surface border-white/10">
      <Flex justifyContent="start" className="gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <Text className="text-slate-400">{title}</Text>
          <Metric className="text-white">{value.toLocaleString()}</Metric>
          {subtitle && <Text className="text-slate-500 text-xs mt-1">{subtitle}</Text>}
        </div>
      </Flex>
    </Card>
  );
}

function PageTypeCard({ type, botVisits, pageViews }) {
  return (
    <div className="p-4 bg-white/5 rounded-lg">
      <Text className="text-slate-400 mb-2">{type}</Text>
      <div className="flex items-center gap-6">
        <div>
          <div className="text-2xl font-bold text-blue-400">{botVisits}</div>
          <div className="text-xs text-slate-500">Bot Visits</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-400">{pageViews}</div>
          <div className="text-xs text-slate-500">Page Views</div>
        </div>
      </div>
    </div>
  );
}

function formatBotName(name) {
  const names = {
    'gptbot': 'GPTBot (OpenAI)',
    'chatgpt-user': 'ChatGPT User',
    'claudebot': 'ClaudeBot (Anthropic)',
    'anthropic': 'Anthropic',
    'perplexitybot': 'PerplexityBot',
    'googlebot': 'Googlebot',
    'bingbot': 'Bingbot',
    'google-extended': 'Google Extended',
    'applebot': 'Applebot',
    'applebot-extended': 'Applebot Extended',
    'duckduckbot': 'DuckDuckBot',
    'yandexbot': 'YandexBot',
    'baiduspider': 'Baiduspider',
  };
  return names[name.toLowerCase()] || name;
}

function getBotCategory(name) {
  const llmBots = ['gptbot', 'chatgpt', 'claude', 'anthropic', 'perplexity', 'cohere', 'google-extended', 'meta', 'youbot', 'ai2bot'];
  const searchBots = ['googlebot', 'bingbot', 'duckduck', 'yandex', 'baidu', 'applebot', 'slurp'];
  
  if (llmBots.some(b => name.includes(b))) return 'llm';
  if (searchBots.some(b => name.includes(b))) return 'search';
  return 'other';
}
